import * as logger from 'loglevel';
import { getUuidMin } from '@front10/helpers/dist/uuid';
import axiosWrapper from '../../helpers/axiosWrapper';
import { NO_DURATION_DEFINED } from '../../helpers/axiosWrapper/cache';

const journeyTypeMap = {
  ROUND_TRIP: 'rt',
  ONE_WAY: 'ow',
  MULTI_CITY: 'mc',
  EACH_WAY: 'ew'
};

/**
 * Makes sure the fares response is complain with a basic schema
 * @param {*} fares
 */
const validateFaresResponse = (fares) => {
  const validation = { isValid: true, message: '' };

  if (!fares || typeof fares !== 'object') {
    validation.isValid = false;
    return {
      isValid: false,
      data: fares,
      message: 'INTEGRATION__FARES: BreakingDeals must be an Array'
    };
  }
  for (const promo in fares) {
    if (fares[promo].length > 0) {
      const fareItem = fares[promo];
      if (
        !fareItem.origin_code ||
        !fareItem.destination_code ||
        !fareItem.departure_start ||
        !fareItem.departure_end ||
        !fareItem.value
      ) {
        logger.warn(
          'INTEGRATION__FARES: Special Offers SCHEMA is invalid, please check API response.',
          fareItem
        );
      }
    }
  }

  return validation;
};

/**
 * Makes sure the fares response is complain with a basic schema
 * @param {*} fares
 */
const validateFaresResponseCarousel = (fares) => {
  const validation = { isValid: true, message: '' };

  if (!fares || typeof fares !== 'object') {
    validation.isValid = false;
    return {
      isValid: false,
      data: fares,
      message: 'INTEGRATION__FARES: BreakingDealsCarousel must be an Array'
    };
  }
  if (fares.length > 0) {
    for (const fare in fares) {
      const fareItem = fares[fare];
      if (
        !fareItem.origin_code ||
        !fareItem.destination_code ||
        !fareItem.departure_start ||
        !fareItem.departure_end ||
        !fareItem.value
      ) {
        logger.warn(
          'INTEGRATION__FARES: Special Offers SCHEMA is invalid, please check API response.',
          fareItem
        );
      }
    }
  }

  return validation;
};

/**
 * The group routes API returns the data in non linear format
 * so this function will intend normalize the data for the components
 * @param {*} faresRawData
 * @param {*} query
 */
const transformDataGrid = (faresRawData, { datePattern, faresLimit }) => {
  let fares = [];
  for (const promo in faresRawData) {
    const fareItems = faresRawData[promo].fares.map((element, index) => {
      const fare = {
        ...element,
        index: getUuidMin(
          `${element.origin_airport}${element.origin_code}${element.destination_airport}${element.destination_code}${element.value}${element.book_until_date}${element.travel_class}${element.min_stay}${element.max_stay}${index}`
        ),
        originCity: element.origin_airport,
        origin: element.origin_code,
        destinationCity: element.destination_airport,
        destination: element.destination_code,
        formattedTotalPrice: element.value,
        journeyType: journeyTypeMap[element.flight_type],
        farenetTravelClass: element.travel_class,
        destinationImage: element.image_url,
        departureDate: element.departure_start,
        returnDate: element.departure_end,
        minStay: element.min_stay,
        maxStay: element.max_stay,
        currencyCode: element.currency,
        fareTerms: element.fare_disclaimer,
        bookUntilDate: element.book_until_date
      };
      return fare;
    });

    fares.push({
      index: getUuidMin(
        `${faresRawData[promo].name}${faresRawData[promo].description}${faresRawData[promo].disclaimer}${faresRawData[promo].terms}`
      ),
      name: faresRawData[promo].name,
      description: faresRawData[promo].description,
      disclaimer: faresRawData[promo].disclaimer,
      terms: faresRawData[promo].terms,
      fares: fareItems
    });
  }
  return fares;
};

/**
 * The group routes API returns the data in non linear format
 * so this function will intend normalize the data for the components
 * @param {*} faresRawData
 * @param {*} query
 */
const transformDataCarousel = (faresRawData, { datePattern, faresLimit }) => {
  const fares = faresRawData.map((element, index) => {
    return {
      ...element,
      index: getUuidMin(
        `${element.origin_airport}${element.origin_code}${element.destination_airport}${element.destination_code}${element.value}${element.book_until_date}${element.travel_class}${element.min_stay}${element.max_stay}${index}`
      ),
      name: element.name,
      description: element.description,
      disclaimer: element.disclaimer,
      terms: element.terms,
      originCity: element.origin_airport,
      origin: element.origin_code,
      destinationCity: element.destination_airport,
      destination: element.destination_code,
      formattedTotalPrice: element.value,
      journeyType: journeyTypeMap[element.flight_type],
      farenetTravelClass: element.travel_class,
      destinationImage: element.image_url,
      departureDate: element.departure_start,
      returnDate: element.departure_end,
      minStay: element.min_stay,
      maxStay: element.max_stay,
      currencyCode: element.currency,
      fareTerms: element.fare_disclaimer,
      bookUntilDate: element.book_until_date
    };
  });

  return fares;
};

/**
 * Retrieve fares from the GroupRoutes Endpoint
 * @param {*} filter
 * @param {*} integrationSettings
 */
const getFares = (filters, integrationSettings, meta, pageContext) => {
  return new Promise((resolve, reject) => {
    if (!integrationSettings) {
      const MESSAGE = `INTEGRATION__FARES: **integrationSettings** not specified.`;
      logger.error(MESSAGE);
      reject(MESSAGE);
    }
    const { v1BreakingDeals: faresApiSettings } = integrationSettings;
    const {
      cache = false,
      cacheDuration = NO_DURATION_DEFINED,
      visualizationType = 'GRID'
    } = faresApiSettings;
    if (!faresApiSettings || !faresApiSettings.method || !faresApiSettings.endPoint) {
      const MESSAGE = `INTEGRATION__FARES: **integrationSettings.specialOffers** configuration issues.`;
      logger.error(MESSAGE);
      reject(MESSAGE);
    }

    const searchParams = new URLSearchParams(
      Object.entries({
        market: filters.market,
        origin: filters.origins,
        destination: filters.destinations,
        travel_class: filters.travelClasses[0] !== 'ALL' ? filters.travelClasses : null,
        budget: filters.budget?.maximum,
        visualization: visualizationType,
        sort: filters.sorting
      })
        .filter(([key, value]) => value != null)
        .map(([key, value]) => [key, value.toString()])
    );
    axiosWrapper(
      {
        method: faresApiSettings.method,
        url: `${faresApiSettings.endPoint}?${searchParams}`,
        headers: { ...faresApiSettings.headers }
      },
      {
        resource: 'INTEGRATION__FARES__SPECIAL__OFFERS',
        meta,
        integrationSettings,
        pageContext,
        cache,
        cacheDuration
      }
    )
      .then((resp) => {
        const validation =
          visualizationType === 'GRID'
            ? validateFaresResponse(resp.data)
            : validateFaresResponseCarousel(resp.data);
        if (validation.isValid) {
          logger.info('INTEGRATION__FARES: Successful request', resp.data);
          const fares =
            visualizationType === 'GRID'
              ? transformDataGrid(resp.data, filters)
              : transformDataCarousel(resp.data, filters);
          resolve(fares);
        } else {
          logger.error(validation.message, validation.data);
          reject(resp.data);
        }
      })
      .catch((err) => {
        reject(err);
      });
  });
};

export { getFares };
